import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { exportGridToCSV } from '../../utils/exportUtils';

export const CurriculumDrilldownModal = ({ filter, onClose, onViewReport, selectedGrade }) => {
  const { getScopedStudents, mathResults, englishResults, loMap } = useData();
  const [resultFilter, setResultFilter] = useState('all'); // 'all' | 'pass' | 'fail'

  if (!filter || !filter.type) return null;
  const scoped = getScopedStudents();

  // Determine questions and subject
  let qList = [];
  let subject = filter.subject || 'Math';
  let requirementInfo = '';

  if (filter.type === 'question') {
    qList = [{ label: `Q${filter.value}`, qNum: filter.value }];
    requirementInfo = `Question ${filter.value}`;
  } else if (filter.type === 'lo') {
    const relevantQs = new Set();
    loMap.forEach((loName, key) => {
      if (loName === filter.value) {
        const parts = key.split('-');
        subject = parts[0];
        relevantQs.add(parseInt(parts[2]));
      }
    });
    qList = Array.from(relevantQs).sort((a, b) => a - b).map(q => ({ label: `Q${q}`, qNum: q }));
    requirementInfo = `Tested on Questions: ${qList.map(q => q.label).join(', ')}`;
  }

  const getFilteredStudents = () => {
    const list = [];
    const resultsMap = subject.toLowerCase() === 'english' ? englishResults : mathResults;

    scoped.forEach(s => {
      if (!s.participated) return;
      const data = resultsMap.get(s.id);

      if (!data) return;

      const answers = qList.map(q => data.answers[q.qNum - 1]);
      if (answers.every(a => isNaN(a) || a === null || a === undefined)) return;

      const correctCount = answers.filter(a => a === 1).length;
      const totalCount = answers.filter(a => !isNaN(a) && a !== null).length;
      const isProficient = totalCount > 0 ? (correctCount / totalCount) >= 0.8 : false;

      if (resultFilter === 'pass' && !isProficient) return;
      if (resultFilter === 'fail' && isProficient) return;

      list.push({
        student: s,
        answers,
        isProficient,
        percentage: totalCount > 0 ? (correctCount / totalCount) * 100 : 0
      });
    });
  window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth', });
    return list.sort((a, b) => a.student.name.localeCompare(b.student.name));
  };

  const students = getFilteredStudents();

  const getTitle = () => {
    if (filter.type === 'question') return `Question ${filter.value} Performance List`;
    return `${filter.value} - Detailed List`;
  };

  const handleExport = () => {
    const headers = ['Student ID', 'Student Name', 'Grade', ...qList.map(q => q.label), 'Score %', 'Proficiency'];
    const rows = students.map(item => [
      item.student.id,
      item.student.name,
      `Grade ${item.student.registeredGrade}`,
      ...item.answers.map(ans => (ans === 1 ? 'Correct' : ans === 0 ? 'Incorrect' : '—')),
      `${item.percentage.toFixed(0)}%`,
      item.isProficient ? 'Proficient' : 'Needs Focus'
    ]);
    exportGridToCSV(headers, rows, `Curriculum_Analysis_Drilldown`);
  };

  return (
    <div className="mt-8 pt-8 border-t-2 border-gray-100 select-none">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex flex-col">
          <h3 className="text-2xl font-bold text-gray-800">{getTitle()}</h3>
          {requirementInfo && (
            <p className="text-sm font-bold text-blue-600 mb-1">{requirementInfo}</p>
          )}
          <p className="text-sm text-gray-500">Detailed student performance for this selection</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={handleExport}
            disabled={students.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold transition-colors cursor-pointer disabled:opacity-50"
          >
            📥 Export Excel
          </button>
          <div className="flex items-center gap-2">
            <label className="text-sm font-bold text-gray-600">Result Filter:</label>
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium cursor-pointer"
            >
              <option value="all">Show All</option>
              <option value="pass">Correct / Proficient</option>
              <option value="fail">Incorrect / Needs Focus</option>
            </select>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-bold transition-colors cursor-pointer"
          >
            ✕ Close List
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-[460px] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="bg-gray-50 text-gray-800 border-b border-gray-200">
              <th className="py-4 px-4 text-left font-bold border-b">Student ID</th>
              <th className="py-4 px-4 text-left font-bold border-b">Name</th>
              <th className="py-4 px-4 text-center font-bold border-b">Grade</th>
              {qList.map(q => (
                <th key={q.label} className="py-4 px-2 text-center font-bold border-b">
                  {q.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan={3 + qList.length} className="text-center py-20 text-gray-400">
                  <div className="flex flex-col items-center">
                    <span className="text-6xl mb-4">🔍</span>
                    <p className="text-lg font-bold">No student records match this filter.</p>
                  </div>
                </td>
              </tr>
            ) : (
              students.filter(({ student }) => Number(student.registeredGrade) === selectedGrade).map(({ student, answers }) => (
                <tr
                  key={student.id}
                  className="hover:bg-gray-50 border-b border-gray-100 transition-colors cursor-pointer"
                  onClick={() => onViewReport(student)}
                >
                  <td className="py-4 px-4 font-bold text-blue-600">{student.id}</td>
                  <td className="py-4 px-4 font-semibold text-gray-800">{student.name}</td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-700">
                    Grade {student.registeredGrade}
                  </td>
                  {answers.map((ans, i) => (
                    <td
                      key={i}
                      className={`py-4 px-2 text-center font-black text-lg ${ans === 1 ? 'text-green-600' : ans === 0 ? 'text-red-500' : 'text-gray-300'
                        }`}
                    >
                      {ans === 1 ? '✓' : ans === 0 ? '✗' : '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default CurriculumDrilldownModal;
